import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
} from "react";
import { ApiRoutes } from "../../routes/routeConstants/apiRoutes";
import Notification from "../../shared/components/Notification";
import LocalStorage from "../../shared/LocalStorage";
import { LocalStorageKeys } from "../../shared/LocalStorage/storageKeys";
import { getNotificationType } from "../../shared/utils/getNotificationType";

const KEY = LocalStorageKeys.SSE_KEY;
const TIMEOUT = 5 * 60 * 1000;

type JobStatusEvent = {
  status: string | null;
  ts: number;
};

type SSEContextType = {
  jobStatus: JobStatusEvent | null;
  startSSE: () => void;
  stopSSE: () => void;
};

const SSEContext = createContext<SSEContextType | null>(null);

const parseSSEData = (data: string) => {
  try {
    return JSON.parse(data);
  } catch {
    return JSON.parse(data.replace(/=>/g, ":"));
  }
};

export const SSEProvider = ({ children }: { children: React.ReactNode }) => {
  const [jobStatus, setJobStatus] = useState<JobStatusEvent | null>(null);

  const esRef = useRef<EventSource | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopSSE = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    esRef.current?.close();
    esRef.current = null;
    setJobStatus(null);
  };

  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(stopSSE, TIMEOUT);
  };


  const startSSE = () => {
    if (esRef.current) return;

    const token = LocalStorage.getItem("access-token");
    if (!token) return;

    esRef.current = new EventSource(
      `${ApiRoutes.SSE_NOTIFICATION}?token=${token}`
    );

    resetTimer();

    esRef.current.onmessage = ({ data }) => {
      resetTimer();
      try {
        const { job_status, message } = parseSSEData(data);
        const type = getNotificationType(job_status);
        if (!type) return;

        setJobStatus({
          status: job_status ?? null,
          ts: Date.now(),
        });

        Notification({
          message: "",
          description: String(message ?? ""),
          type,
        });
      } catch {
        stopSSE();
      }
    };

    esRef.current.onerror = ()=>{
      stopSSE()
    };
  };

  useEffect(() => {
    const shouldStart = LocalStorage.getItem(KEY);
    if (shouldStart) {
      startSSE();
    }
  }, []);

  return (
    <SSEContext.Provider value={{ jobStatus, startSSE, stopSSE }}>
      {children}
    </SSEContext.Provider>
  );
};

export const useSSE = () => {
  const ctx = useContext(SSEContext);
  if (!ctx) {
    throw new Error("useSSE must be used inside SSEProvider");
  }
  return ctx;
};
